import { FetchResult, MutationResult } from '@apollo/client';
import { UsageLimitEntitlementName } from '@jupiterone/entitlement-types';
import { QuestionRuleInstance } from '@jupiterone/jupiter-types';
import {
  createUseMutationHook,
  createUseQueryHook,
  GetCumulativeAlertsEmailReportRuleInstancesResult,
  GetCumulativeAlertsEmailReportRuleInstances,
  CheckUsageLimitEntitlementAccess,
  QuestionRuleInstanceWithTags,
  ListRuleInstancesQuery,
} from '@jupiterone/jupiter-web-toolkit';
import {
  createQuestionRuleInstance,
  deleteQuestionRuleInstance,
  evaluateRule,
  updateQuestionRuleInstance,
} from 'jupiter-web-alerts/src/graphql/mutations';
import {
  GetRule,
  QuestionRuleTags,
} from 'jupiter-web-alerts/src/graphql/queries';
import {
  CreateRuleInstanceResponse,
  UpdateRuleInstanceResponse,
} from 'jupiter-web-alerts/src/types';
import { RulesListContext } from '@jupiterone/jupiter-web-toolkit/src/context/RulesListContext';
import { useContext } from 'react';

interface DeleteRuleInstanceResponse {
  deleteRuleInstance: {
    id: string;
  };
}

export interface QuestionRuleTagsResponse {
  questionRuleTags: string[];
}

const standardRefetchQueries = [
  { query: ListRuleInstancesQuery },
  {
    query: CheckUsageLimitEntitlementAccess,
    variables: {
      usageLimitEntitlementName: UsageLimitEntitlementName.CUSTOM_ALERT_RULES,
    },
  },
];

export const useRule = createUseQueryHook<
  {
    questionRuleInstance: QuestionRuleInstanceWithTags;
  },
  { id: string }
>(GetRule);

export const useQuestionRuleTags = createUseQueryHook<
  QuestionRuleTagsResponse,
  undefined
>(QuestionRuleTags);

export const useCumulativeAlertsEmailReportRuleInstances = createUseQueryHook<
  GetCumulativeAlertsEmailReportRuleInstancesResult,
  undefined
>(GetCumulativeAlertsEmailReportRuleInstances);

export const useEvaluateRule = createUseMutationHook<
  { evaluateRuleInstance: { id: string } },
  { id: string }
>(evaluateRule);

const useCreateQuestionRuleInstanceMutation = createUseMutationHook<
  CreateRuleInstanceResponse,
  { instance: Partial<QuestionRuleInstance> }
>(createQuestionRuleInstance);

export function useCreateQuestionRuleInstance(): [
  (
    rule: Partial<QuestionRuleInstance>
  ) => Promise<FetchResult<CreateRuleInstanceResponse>>,
  MutationResult<CreateRuleInstanceResponse>
] {
  // since this is used in other SPAs, calling useContext directly here to avoid Provider issues
  const contextValue = useContext(RulesListContext);
  const [createRule, result] = useCreateQuestionRuleInstanceMutation();

  return [
    (rule) =>
      createRule({
        variables: {
          instance: rule,
        },
        update: contextValue?.getRuleUpdater<CreateRuleInstanceResponse>(
          (cacheEntry, mutationResult) => [
            mutationResult.createQuestionRuleInstance,
            ...cacheEntry,
          ]
        ),
        // don't refetch the rules because the new rule would be moved
        // from the top of the list
        refetchQueries: [standardRefetchQueries[1]],
      }),
    result,
  ];
}

const useUpdateQuestionRuleInstanceMutation = createUseMutationHook<
  UpdateRuleInstanceResponse,
  { instance: Partial<QuestionRuleInstance> }
>(updateQuestionRuleInstance);

export function useUpdateQuestionRuleInstance(): [
  (
    rule: Partial<QuestionRuleInstanceWithTags>
  ) => Promise<FetchResult<UpdateRuleInstanceResponse>>,
  MutationResult<UpdateRuleInstanceResponse>
] {
  const contextValue = useContext(RulesListContext);
  const [updateRule, result] = useUpdateQuestionRuleInstanceMutation();

  return [
    (rule) =>
      updateRule({
        variables: {
          instance: rule,
        },
        update: contextValue?.getRuleUpdater<UpdateRuleInstanceResponse>(
          (cacheEntry, mutationResult) =>
            cacheEntry.map((oldRule) =>
              oldRule.id === rule.id
                ? mutationResult.updateQuestionRuleInstance
                : oldRule
            )
        ),
        refetchQueries: standardRefetchQueries,
      }),
    result,
  ];
}

const useDeleteQuestionRuleInstanceMutation = createUseMutationHook<
  DeleteRuleInstanceResponse,
  { id: string }
>(deleteQuestionRuleInstance);

export function useDeleteQuestionRuleInstance(): [
  (id: string) => Promise<FetchResult<DeleteRuleInstanceResponse>>,
  MutationResult<DeleteRuleInstanceResponse>
] {
  const contextValue = useContext(RulesListContext);
  const [deleteRule, result] = useDeleteQuestionRuleInstanceMutation();

  return [
    (id) =>
      deleteRule({
        variables: {
          id,
        },
        update: contextValue?.getRuleUpdater((cacheEntry) =>
          cacheEntry.filter((rule) => rule.id !== id)
        ),
        refetchQueries: standardRefetchQueries,
      }),
    result,
  ];
}
